import React, { useEffect, useMemo, useState } from 'react';
import './availability.css';

async function jsonFetch(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    throw new Error(payload.error || response.statusText);
  }
  return payload;
}

async function getAvailabilityRules({ apiBase, clinician_uuid }) {
  try {
    const result = await jsonFetch(
      await fetch(`${apiBase}/Clinician/availability/rules?clinician_uuid=${clinician_uuid}`)
    );
    return result.rules || [];
  } catch (err) {
    console.error('Failed to load availability rules:', err);
    return [];
  }
}

async function upsertAvailabilityRules({ apiBase, clinician_uuid, rules }) {
  await jsonFetch(
    await fetch(`${apiBase}/Clinician/availability/rules/upsert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clinician_uuid, rules }),
    })
  );
}

async function addUnavailability({ apiBase, clinician_uuid, slot_start_utc, slot_end_utc }) {
  await jsonFetch(
    await fetch(`${apiBase}/Clinician/consultations/unavailability`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clinician_uuid, slot_start_utc, slot_end_utc }),
    })
  );
}

async function getUnavailabilities({ apiBase, clinician_uuid }) {
  try {
    const start = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(); // 90 days ago
    const end = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(); // 1 year ahead
    const qs = new URLSearchParams({ clinician_uuid, start, end }).toString();
    const url = `${apiBase}/Clinician/consultations/clinician-bookings?${qs}`;
    const res = await jsonFetch(await fetch(url));
    const bookings = res.bookings || [];
    return bookings.filter(b => b.event_type === 'unavailability');
  } catch (err) {
    console.error('Failed to load unavailabilities:', err);
    return [];
  }
}

async function deleteUnavailability({ apiBase, unavailability_uuid, clinician_uuid }) {
  await jsonFetch(
    await fetch(`${apiBase}/Clinician/consultations/unavailability/${unavailability_uuid}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clinician_uuid })
    })
  );
}

export default function AvailabilityTab({ clinician_uuid, apiBase }) {
  const [rules, setRules] = useState(
    () => Array.from({ length: 7 }, () => ({ start: '09:00', end: '17:00', is_active: false }))
  );
  const [originalRules, setOriginalRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const days = useMemo(() => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], []);
  const [unavailStart, setUnavailStart] = useState('');
  const [unavailEnd, setUnavailEnd] = useState('');
  const [unavailabilities, setUnavailabilities] = useState([]);

  const loadUnavailabilities = async () => {
    const unavails = await getUnavailabilities({ apiBase, clinician_uuid });
    setUnavailabilities(unavails);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const fetchedRules = await getAvailabilityRules({ apiBase, clinician_uuid });
        const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const nextRules = days.map((_, dayIndex) => {
          const baseRule = {
            day_of_week: dayIndex,
            start: '09:00',
            end: '17:00',
            is_active: false,
            timezone: localTimezone,
          };
          const saved = fetchedRules.find((rule) => (rule.day_of_week % 7) === dayIndex);
          if (saved) {
            baseRule.is_active = Boolean(saved.is_active);
            baseRule.start = saved.start_time_utc?.slice(0, 5) || '09:00';
            baseRule.end = saved.end_time_utc?.slice(0, 5) || '17:00';
          }
          return baseRule;
        });

        setRules(nextRules);
        setOriginalRules(JSON.parse(JSON.stringify(nextRules))); // Deep copy
        await loadUnavailabilities();
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase, clinician_uuid, days]);

  const countChanges = () => {
    let changes = 0;
    rules.forEach((rule, index) => {
      const original = originalRules[index];
      if (
        rule.is_active !== original?.is_active ||
        rule.start !== original?.start ||
        rule.end !== original?.end
      ) {
        changes++;
      }
    });
    return changes;
  };

  const hasChanges = countChanges() > 0;
  const changeCount = countChanges();

  const save = async () => {
    setSaving(true);
    const payload = rules.map((rule) => ({
      day_of_week: rule.day_of_week,
      start_time_utc: rule.start,
      end_time_utc: rule.end,
      is_active: rule.is_active,
    }));

    try {
      await upsertAvailabilityRules({ apiBase, clinician_uuid, rules: payload });
      setOriginalRules(JSON.parse(JSON.stringify(rules))); // Update original after save
      alert('Availability Saved!');
    } catch (err) {
      alert(`Error saving: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const addUnavail = async () => {
    if (!unavailStart || !unavailEnd) {
      alert('Please select both a start and end time.');
      return;
    }
    const startDate = new Date(unavailStart);
    const endDate = new Date(unavailEnd);
    if (endDate <= startDate) {
      alert('End time must be after the start time.');
      return;
    }
    try {
      await addUnavailability({
        apiBase,
        clinician_uuid,
        slot_start_utc: startDate.toISOString(),
        slot_end_utc: endDate.toISOString(),
      });
      setUnavailStart('');
      setUnavailEnd('');
      await loadUnavailabilities();
      alert('Unavailability block added successfully!');
    } catch (err) {
      alert(`Error adding unavailability: ${err.message}`);
    }
  };

  const removeUnavail = async (unavailability_uuid) => {
    if (!window.confirm('Are you sure you want to remove this unavailability block?')) return;
    try {
      await deleteUnavailability({ apiBase, unavailability_uuid, clinician_uuid });
      await loadUnavailabilities();
      alert('Unavailability block removed successfully!');
    } catch (err) {
      alert(`Error removing unavailability: ${err.message}`);
    }
  };

  if (loading) {
    return <div className="clinician-pad">Loading Availability...</div>;
  }

  const now = new Date();
  const upcomingUnavails = unavailabilities
    .filter(u => new Date(u.slot_end_utc) >= now)
    .sort((a, b) => new Date(a.slot_start_utc) - new Date(b.slot_start_utc));
  const pastUnavails = unavailabilities
    .filter(u => new Date(u.slot_end_utc) < now)
    .sort((a, b) => new Date(b.slot_start_utc) - new Date(a.slot_start_utc));

  const formatDateTime = (utcString) => {
    const date = new Date(utcString);
    return date.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeOnly = (utcString) => {
    const date = new Date(utcString);
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="clinician-availability-layout">
      <div className="clinician-card">
        <div className="clinician-title">Weekly Availability</div>
    
        <div className="clinician-rules-grid">
          {rules.map((rule, index) => (
            <div key={index} className="clinician-rule-row">
              <div className="clinician-rule-day">{days[index]}</div>
              <label className="clinician-rule-toggle">
                <input
                  type="checkbox"
                  checked={rule.is_active}
                  onChange={(event) =>
                    setRules((prev) =>
                      prev.map((entry, entryIndex) =>
                        entryIndex === index ? { ...entry, is_active: event.target.checked } : entry
                      )
                    )
                  }
                />
                <span>Active</span>
              </label>
              <div className="clinician-rule-timepair">
                <input
                  type="time"
                  value={rule.start}
                  disabled={!rule.is_active}
                  onChange={(event) =>
                    setRules((prev) =>
                      prev.map((entry, entryIndex) =>
                        entryIndex === index ? { ...entry, start: event.target.value } : entry
                      )
                    )
                  }
                />
                <span>-</span>
                <input
                  type="time"
                  value={rule.end}
                  disabled={!rule.is_active}
                  onChange={(event) =>
                    setRules((prev) =>
                      prev.map((entry, entryIndex) =>
                        entryIndex === index ? { ...entry, end: event.target.value } : entry
                      )
                    )
                  }
                />
              </div>
            </div>
          ))}
        </div>
        <div className="clinician-card-actions">
          <button
            className="clinician-btn-primary clinician-btn-with-badge"
            onClick={save}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Rules'}
            {!saving && hasChanges && <span className="clinician-change-badge">{changeCount}</span>}
          </button>
        </div>
      </div>

      <div className="clinician-card full-height">
        <div className="clinician-title">One-Time Unavailability</div>
        <div className="clinician-unavail-form">
          <label>From</label>
          <input type="datetime-local" value={unavailStart} onChange={(event) => setUnavailStart(event.target.value)} />
          <label>To</label>
          <input type="datetime-local" value={unavailEnd} onChange={(event) => setUnavailEnd(event.target.value)} />
          <button className="clinician-btn-primary" onClick={addUnavail}>Add Block</button>
        </div>
        <p className="clinician-hint">Block out specific times for appointments or holidays.</p>

        {upcomingUnavails.length > 0 && (
          <>
            <div className="clinician-section-header">Upcoming Unavailabilities</div>
            <div className="clinician-unavail-list">
              {upcomingUnavails.map((unavail) => (
                <div key={unavail.event_id} className="clinician-unavail-item">
                  <div className="clinician-unavail-info">
                    <div className="clinician-unavail-time">
                      {formatDateTime(unavail.slot_start_utc)}
                    </div>
                    <div className="clinician-unavail-date">
                      Ends: {formatDateTime(unavail.slot_end_utc)}
                    </div>
                  </div>
                  <div className="clinician-unavail-actions">
                    <button
                      className="clinician-btn-danger"
                      onClick={() => removeUnavail(unavail.event_id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {pastUnavails.length > 0 && (
          <>
            <hr className="clinician-section-divider" />
            <div className="clinician-section-header">Past Unavailabilities</div>
            <div className="clinician-unavail-list">
              {pastUnavails.map((unavail) => (
                <div key={unavail.event_id} className="clinician-unavail-item past">
                  <div className="clinician-unavail-info">
                    <div className="clinician-unavail-time">
                      {formatDateTime(unavail.slot_start_utc)}
                    </div>
                    <div className="clinician-unavail-date">
                      Ended: {formatDateTime(unavail.slot_end_utc)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
